import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import { Button, colors } from 'src/components/app-ui'
import { updateCourseCodeService } from 'src/api/course'
import { coursePatchSuccess } from 'src/reducers/course/action'
import { AssignmentFieldNames } from 'src/model/assignment'
import { splitCourseCode, normalizeCourseSlug } from 'src/utils/course-code'

const useStyles = makeStyles({
  root: {
    maxWidth: 640,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.navy,
  },
  row: {
    display: 'flex',
    gap: '8px',
  },
  field: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${colors.borderSoft}`,
    borderRadius: '8px',
    background: '#FFFFFF',
    '&:focus-within': {
      borderColor: colors.primary,
    },
  },
  input: {
    flex: 1,
    minWidth: 0,
    padding: '11px 12px',
    border: 0,
    outline: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: '14px',
    color: colors.navy,
  },
  suffix: {
    paddingRight: 12,
    fontSize: '14px',
    color: colors.subtle,
  },
  help: {
    fontSize: '12px',
    color: colors.muted,
  },
  error: {
    color: '#D4351C',
  },
})

/**
 * Editable part of the course URL (the slug before the numeric suffix).
 * Saved explicitly, because the old link stops working after a change.
 */
const CourseUrlField = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const course = useSelector(state => state.course.course)
  const code = course?.[AssignmentFieldNames.CODE]
  const courseCode = splitCourseCode(code)
  const [slug, setSlug] = useState(courseCode ? courseCode.slug : '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setSlug(courseCode ? courseCode.slug : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  if (!courseCode) return null

  const normalized = normalizeCourseSlug(slug)
  // A code already saved without a slug (e.g. "-61564577") must stay valid.
  const isEmpty = normalized === '' && courseCode.slug !== ''
  const changed = normalized !== normalizeCourseSlug(courseCode.slug)

  const save = async () => {
    if (isEmpty || !changed) return
    setSaving(true)
    try {
      const response = await updateCourseCodeService(
        course[AssignmentFieldNames.ID],
        normalized,
      )
      // Codes without a suffix get a new one from the backend.
      const newCode =
        response?.data?.code ??
        response?.code ??
        (courseCode.suffix && `${normalized}-${courseCode.suffix}`)
      if (newCode) {
        dispatch(
          coursePatchSuccess({
            _contents: [{ path: AssignmentFieldNames.CODE, value: newCode }],
          }),
        )
      }
      enqueueSnackbar('Course URL updated.', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.operationMessage ||
          'Could not update the course URL.',
        { variant: 'error' },
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={classes.root}>
      <span className={classes.label}>Course URL</span>
      <div className={classes.row}>
        <label className={classes.field}>
          <input
            className={classes.input}
            value={slug}
            onChange={event =>
              setSlug(
                normalizeCourseSlug(event.target.value, {
                  keepTrailingHyphen: true,
                }),
              )
            }
            onBlur={() => setSlug(value => value.replace(/-+$/, ''))}
            onKeyDown={event => event.key === 'Enter' && save()}
          />
          {courseCode.suffix && (
            <span className={classes.suffix}>-{courseCode.suffix}</span>
          )}
        </label>
        {changed && (
          <Button size="small" disabled={isEmpty || saving} onClick={save}>
            Save URL
          </Button>
        )}
      </div>
      <span className={`${classes.help} ${isEmpty ? classes.error : ''}`}>
        {isEmpty
          ? 'Enter at least one letter or number.'
          : 'Only the part before the number can change. Use lowercase letters, numbers and hyphens. Your old link will stop working after you save.'}
      </span>
    </div>
  )
}

export default CourseUrlField
